import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { TableSearchPreferencesConfig } from "../ConfigRecords";

/**
 * Valid config keys for search table preferences
 */
export type SearchTablePreferenceKey =
  | "dataset_search_table_preferences"
  | "file_search_table_preferences"
  | "topic_search_table_preferences"
  | "message_path_search_table_preferences"
  | "event_search_table_preferences";

/**
 * Generate query key for a specific search table preference
 */
export function getQueryKey(configKey: SearchTablePreferenceKey) {
  return ["searchTablePreferences", configKey] as const;
}

/**
 * Generic hook to fetch search table preferences for a specific config key.
 * Returns table preferences including columns and rows per page settings.
 *
 * @param configKey - The config key identifying which table preferences to fetch
 * @returns React Query result with table preferences data
 */
export function useSearchTablePreferences(configKey: SearchTablePreferenceKey) {
  const { configService } = useDomainServices();

  return useQuery({
    queryKey: getQueryKey(configKey),
    queryFn: async ({ signal }) => {
      const value =
        await configService.getUserConfig<TableSearchPreferencesConfig>(
          configKey,
          {
            abortSignal: signal,
          },
        );

      if (value === null) {
        // Return default empty config if not found
        const config: TableSearchPreferencesConfig = {
          version: 1,
          org_scoped_preferences: {},
        };

        return config;
      }

      return value;
    },
  });
}
